import { timeToMinutes } from './utils';
import { SessionWithPsychologist } from './data';

export function doesOverlapExistingTemplate(
  template: { id: string; weekday: number; startTime: string; endTime: string; active: boolean }[],
  weekdayIdx: number,
  startMin: number,
  endMin: number
): boolean {
  if (!Number.isFinite(startMin) || !Number.isFinite(endMin)) return false;
  const dayRanges = template.filter(t => t.active && t.weekday === weekdayIdx);
  for (const t of dayRanges) {
    const tStart = timeToMinutes(t.startTime);
    let tEnd = timeToMinutes(t.endTime);
    
    if (tEnd === 0) tEnd = 24 * 60;
    
    if (startMin < tEnd && endMin > tStart) return true;
  }
  return false;
}

export function isLocalRangeWithinTemplate(
  template: { id: string; weekday: number; startTime: string; endTime: string; active: boolean }[],
  startOfWeek: Date,
  dateStr: string,
  startTimeStr: string,
  durationMin: number
): boolean {
  const [Y, M, D] = dateStr.split('-').map(Number);
  const localDate = new Date(Y || 1970, (M || 1) - 1, D || 1);
  localDate.setHours(0, 0, 0, 0);
  const start0 = new Date(startOfWeek);
  start0.setHours(0, 0, 0, 0);
  const weekdayLocalIdx = Math.max(0, Math.min(6, Math.round((localDate.getTime() - start0.getTime()) / 86400000)));
  const startMin = timeToMinutes(startTimeStr);
  const endMin = startMin + Math.max(0, durationMin);

  const dayRanges = template
    .filter(t => t.active && t.weekday === weekdayLocalIdx)
    .map(t => {
      const start = timeToMinutes(t.startTime);
      let end = timeToMinutes(t.endTime);
      
      if (end === 0) end = 24 * 60;
      
      return { start, end };
    })
    .sort((a, b) => a.start - b.start);
  
  if (dayRanges.length === 0) return false;
  
  const mergedRanges: Array<{ start: number; end: number }> = [];
  
  for (const range of dayRanges) {
    if (mergedRanges.length === 0) {
      mergedRanges.push({ start: range.start, end: range.end });
    } else {
      const last = mergedRanges[mergedRanges.length - 1];
      if (range.start <= last.end) {
        last.end = Math.max(last.end, range.end);
      } else {
        mergedRanges.push({ start: range.start, end: range.end });
      }
    }
  }
  
  // Basta con que la sesión SOLAPE cualquier parte de un bloque de disponibilidad: puede arrancar
  // antes (tomando un pedazo) o dentro y extenderse más allá. Solo se rechaza si queda 100% fuera.
  for (const merged of mergedRanges) {
    if (startMin < merged.end && endMin > merged.start) {
      return true;
    }
  }

  return false;
}

export function mapTemplateToLocalHours(
  template: { id: string; weekday: number; startTime: string; endTime: string; active: boolean }[],
  startOfWeek: Date,
  weekdayIdx: number,
  timeStr: string
): boolean {
  const base = new Date(startOfWeek);
  const localDate = new Date(base);
  localDate.setDate(base.getDate() + weekdayIdx);
  const start0 = new Date(startOfWeek);
  start0.setHours(0, 0, 0, 0);
  const weekdayLocalIdxCalc = Math.max(0, Math.min(6, Math.round((localDate.getTime() - start0.getTime()) / 86400000)));
  const minLocal = timeToMinutes(timeStr);
  return template.some(t => {
    if (!t.active) return false;
    if (t.weekday !== weekdayLocalIdxCalc) return false;
    const start = timeToMinutes(t.startTime);
    let end = timeToMinutes(t.endTime);
    
    if (end === 0) end = 24 * 60;
    
    return minLocal >= start && minLocal < end;
  });
}

export function hasSessionOverlapFast(
  sessions: SessionWithPsychologist[],
  startMin: number,
  endMin: number
): boolean {
  return sessions.some(s => {
    const sStart = timeToMinutes(s.time);
    const sEnd = sStart + 60;
    return sStart < endMin && sEnd > startMin;
  });
}

export function findAdjacentSchedules(
  template: { id: string; weekday: number; startTime: string; endTime: string; active: boolean; scheduleType?: string }[],
  weekdayIdx: number,
  startTime: string,
  endTime: string,
  scheduleType?: string,
): { above: typeof template[0] | null; below: typeof template[0] | null } {
  const dayRanges = template.filter(t => t.active && t.weekday === weekdayIdx);
  const startMin = timeToMinutes(startTime);
  const endMin = timeToMinutes(endTime);
  const effectiveStart = startMin === 0 ? 0 : startMin;
  const effectiveEnd = endMin === 0 ? 1440 : endMin;

  let above: typeof template[0] | null = null;
  let below: typeof template[0] | null = null;

  for (const t of dayRanges) {
    // Don't merge ranges with different schedule types
    if (scheduleType && (t.scheduleType ?? 'standard') !== scheduleType) continue;
    const tEnd = timeToMinutes(t.endTime) === 0 ? 1440 : timeToMinutes(t.endTime);
    const tStart = timeToMinutes(t.startTime);
    if (tEnd === effectiveStart) above = t;
    if (tStart === effectiveEnd) below = t;
  }

  return { above, below };
}

export function convertFrontendDayToBackend(dayIdxMon0: number): number {
  const d = Math.trunc(dayIdxMon0);
  if (d >= 0 && d <= 6) return ((d % 7) + 1);
  return 1;
}

